import React, { useMemo, useState } from 'react';
import {
  HoverCardContent,
  HoverCardRoot,
  HoverCardTrigger,
} from '@/components/ui/HoverCard';
import { Button, DataCell, DataList, Flex } from '@/components/ui';
import { FilePreviewModal } from '@/components/file-preview';
import { getCsvFileDownloadEndpoint } from '@/utils/downloadEndpoints';
import { IconEye } from '@tabler/icons-react';
import type { CsvFile } from '@/types/csvFiles';

interface CsvFileHoverCardProps {
  file: CsvFile;
  children: React.ReactNode;
  columns?: number;
  rows?: number;
}

export const CsvFileHoverCard: React.FC<CsvFileHoverCardProps> = ({
  file,
  children,
  columns,
  rows,
}) => {
  const [previewModalOpen, setPreviewModalOpen] = useState(false);

  const dataListItems = useMemo(() => {
    const items: Array<
      { label: string; value: React.ReactNode } | { type: 'separator' }
    > = [
      { type: 'separator' },
      {
        label: 'Size',
        value: (
          <DataCell noWrapper type="fileSize" bytes={file.size} size="small" />
        ),
      },
    ];

    if (columns !== undefined) {
      items.push({
        label: 'Columns',
        value: (
          <DataCell noWrapper type="number" value={columns} size="small" />
        ),
      });
    }

    if (rows !== undefined) {
      items.push({
        label: 'Rows',
        value: (
          <DataCell
            noWrapper
            type="number"
            value={rows}
            size="small"
          />
        ),
      });
    }

    // Add separator between file details and upload information
    items.push({ type: 'separator' });

    items.push({
      label: 'Uploaded By',
      value: (
        <DataCell
          noWrapper
          type="user"
          user={file.user || undefined}
          size="small"
        />
      ),
    });

    items.push({
      label: 'Uploaded',
      value: (
        <DataCell
          noWrapper
          type="datetime"
          value={file.created_at}
          relative={false}
          format="MMM D, YYYY [at] h:mm A"
          size="small"
        />
      ),
    });

    return items;
  }, [file, columns, rows]);

  return (
    <>
      <HoverCardRoot>
        <HoverCardTrigger>{children}</HoverCardTrigger>
        <HoverCardContent size="2">
          <Flex direction="column" gap="3" style={{ minWidth: '280px' }}>
            {/* Header */}
            <DataCell noWrapper type="fileName" filename={file.filename} />

            {/* File Details */}
            <DataList
              items={dataListItems}
              size="medium"
              orientation="horizontal"
              alignValues="end"
            />

            {/* Preview Button */}
            <Button
              variant="outlined"
              size="small"
              startIcon={<IconEye />}
              onClick={() => setPreviewModalOpen(true)}
              style={{ width: '100%' }}
            >
              Preview File
            </Button>
          </Flex>
        </HoverCardContent>
      </HoverCardRoot>

      {/* File Preview Modal */}
      <FilePreviewModal
        apiEndpoint={getCsvFileDownloadEndpoint(file)}
        filename={file.filename}
        mimeType="text/csv"
        fileSize={file.size}
        open={previewModalOpen}
        onOpenChange={setPreviewModalOpen}
        title={`Preview: ${file.filename}`}
        showDownloadButton={false}
      />
    </>
  );
};
